import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ImagePlus, Star, Trash2, GripVertical } from "lucide-react";

interface VenueImageManagerProps {
  venueId: string;
}

interface VenueImage {
  id: string;
  venue_id: string;
  image_url: string;
  is_main: boolean;
  sort_order: number;
  created_at: string;
}

export function VenueImageManager({ venueId }: VenueImageManagerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const { data: images, isLoading } = useQuery({
    queryKey: ["venue-images", venueId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("venue_images")
        .select("*")
        .eq("venue_id", venueId)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data as VenueImage[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (image: VenueImage) => {
      // Extract path from URL
      const url = new URL(image.image_url);
      const pathParts = url.pathname.split("/storage/v1/object/public/venue-images/");
      if (pathParts[1]) {
        await supabase.storage.from("venue-images").remove([pathParts[1]]);
      }
      const { error } = await supabase.from("venue_images").delete().eq("id", image.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["venue-images", venueId] });
      toast({ title: "Imagen eliminada" });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const setMainMutation = useMutation({
    mutationFn: async (imageId: string) => {
      // Unset all as main
      await supabase
        .from("venue_images")
        .update({ is_main: false } as any)
        .eq("venue_id", venueId);
      // Set the selected one
      const { error } = await supabase
        .from("venue_images")
        .update({ is_main: true } as any)
        .eq("id", imageId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["venue-images", venueId] });
      toast({ title: "Imagen de portada actualizada" });
    },
  });

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const currentCount = images?.length ?? 0;
      const isFirst = currentCount === 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const ext = file.name.split(".").pop();
        const fileName = `${venueId}/${Date.now()}-${i}.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from("venue-images")
          .upload(fileName, file);
        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("venue-images")
          .getPublicUrl(fileName);

        const { error: insertError } = await supabase.from("venue_images").insert({
          venue_id: venueId,
          image_url: urlData.publicUrl,
          is_main: isFirst && i === 0,
          sort_order: currentCount + i,
        });
        if (insertError) throw insertError;
      }

      queryClient.invalidateQueries({ queryKey: ["venue-images", venueId] });
      toast({ title: `${files.length} imagen(es) subida(s)` });
    } catch (err: any) {
      toast({ title: "Error al subir", description: err.message, variant: "destructive" });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Imágenes ({images?.length ?? 0})</p>
        <Button variant="outline" size="sm" disabled={uploading} asChild>
          <label className="cursor-pointer">
            <ImagePlus className="mr-2 h-4 w-4" />
            {uploading ? "Subiendo…" : "Añadir fotos"}
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
            />
          </label>
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : !images?.length ? (
        <p className="text-sm text-muted-foreground">Sin imágenes. Sube la primera foto.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {images.map((img) => (
            <div
              key={img.id}
              className="group relative overflow-hidden rounded-lg border aspect-[4/3]"
            >
              <img
                src={img.image_url}
                alt="Foto de sala"
                className="h-full w-full object-cover"
              />
              {img.is_main && (
                <span className="absolute left-1 top-1 rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
                  Portada
                </span>
              )}
              <div className="absolute inset-0 flex items-end justify-end gap-1 bg-gradient-to-t from-black/50 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
                {!img.is_main && (
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setMainMutation.mutate(img.id)}
                    title="Establecer como principal"
                  >
                    <Star className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  variant="destructive"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => deleteMutation.mutate(img)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
